import { useState } from "react";
import { COLORS } from "../../utils/colors";
import { ClickableName } from "../common/EmployeeNameLink";

function getScoreColor(score) {
  if (score >= 7.0) return COLORS.green;
  if (score >= 5.0) return COLORS.amber;
  return COLORS.red;
}
function getAssessment(score) {
  if (score >= 9.0) return "Exceptional";
  if (score >= 8.0) return "Strong";
  if (score >= 7.0) return "Good";
  if (score >= 5.0) return "Adequate";
  return "Marginal";
}

const sampleData = {
  opportunity: { name: "Cloud Migration Initiative - Phase 2", client: "Northern Trust Bank", industry: "Financial Services", stage: "NEGOTIATION", winProb: 75, weightedValue: 72345 },
  team: [
    { name: "Todd Korando",   role: "DevOps Engineer",           availability: 100 },
    { name: "Shirley Cortez", role: "SQL Developer",             availability: 64  },
    { name: "Michael Monske", role: "Site Reliability Engineer", availability: 100 },
  ],
  scores: {
    overall:      7.2,
    technical:    { score: 7.2, summary: "Good coverage, PMP cert gap on SRE role" },
    chemistry:    { score: 5.1, summary: "100% pair coverage, DISC needs context" },
    financial:    { pct: "50.6%", summary: "50.6% blended margin", score: 8.0 },
    availability: { score: 9.5, summary: "Near-perfect — minor Cortez ramp-up" },
    experience:   { score: 3.9, summary: "Korando + Monske strong, Cortez DIR concern" },
  },
  strengths: [
    "100% collaboration pair coverage — all 3 pairs have worked together, all within 12 months",
    "Korando has prior Northern Trust Bank experience — Market Intelligence System Pilot (2023)",
    "Near-perfect availability — Korando and Monske at 100%, team ready for Oct 1 start",
  ],
  tradeoffs: [
    { concern: "Cortez negative DIR (-0.09) across 28 projects — below-average delivery track record", severity: "Medium", mitigation: "Proven collaboration with both teammates" },
    { concern: "PMP certification gap on SRE role — no team member holds PMP", severity: "Low", mitigation: "Monske has CBAP and Lean Six Sigma Black Belt" },
    { concern: "Korando and Monske priced 18-28% below historical billing rates", severity: "Low-Medium", mitigation: "Confirm pricing strategy with sales" },
  ],
  mitigationPlan: [
    { risk: "Cortez negative DIR",      action: "Assign Korando as peer reviewer for Cortez deliverables weeks 1-3. Weekly quality check-in with engagement manager.", owner: "Engagement Manager + Korando", timing: "Pre-kickoff + weeks 1-3",      success: "Deliverables pass review without rework in 2/3 milestones",     severity: "yellow" },
    { risk: "PMP certification gap",    action: "Confirm if PMP is contractually required or preferred. Monske's CBAP + Lean Six Sigma may satisfy the intent.",        owner: "Resource Manager",            timing: "Before staffing commitment",  success: "Client confirms cert requirement satisfied",                      severity: "yellow" },
    { risk: "Below-historical pricing", action: "Validate with sales owner that role levels align with staffing seniority. Confirm pricing is intentional.",            owner: "Resource Manager + Sales",    timing: "Before proposal submission",  success: "Written confirmation pricing intentional",                        severity: "yellow" },
    { risk: "Staggered start",          action: "Use Korando's solo weeks for environment setup and client relationship building. Create onboarding package for Cortez and Monske.", owner: "Korando + Engagement Manager", timing: "Week 1", success: "Onboarding doc ready before Cortez joins Oct 15", severity: "yellow" },
  ],
  recommendation: "Staff this team with confidence. The combination of 100% collaboration coverage, Korando's prior client experience, and Monske's exceptional DIR makes this a strong recommendation despite Cortez's below-average DIR. The team chemistry advantage outweighs the individual performance concern. Watch Cortez's delivery quality closely.",
};

function MemberAvatar({ name }) {
  const parts = (name || "").trim().split(/\s+/);
  const first = parts[0] || "";
  const last = parts[parts.length - 1] || "";
  const initials = `${first.charAt(0)}${last !== first ? last.charAt(0) : ""}`.toUpperCase();
  const [imgError, setImgError] = useState(false);
  const src = !imgError && first && last && last !== first
    ? `/avatars/${first.toLowerCase()}_${last.toLowerCase()}_avatar.png`
    : null;

  return src ? (
    <img
      src={src}
      alt={name}
      onError={() => setImgError(true)}
      style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
    />
  ) : (
    <div style={{
      width: 32, height: 32, borderRadius: "50%",
      background: COLORS.purple,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function ScoreGauge({ score, size = "large" }) {
  const color = getScoreColor(score);
  const assessment = getAssessment(score);
  const isLarge = size === "large";
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{
        width: isLarge ? 140 : 72, height: isLarge ? 140 : 72, borderRadius: "50%",
        border: `${isLarge ? 5 : 3}px solid ${color}`,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        margin: "0 auto", background: `${color}15`,
      }}>
        <span style={{ fontSize: isLarge ? 42 : 20, fontWeight: 800, color, lineHeight: 1 }}>{score.toFixed(1)}</span>
      </div>
      {isLarge && (
        <div style={{ marginTop: 10, fontSize: 18, fontWeight: 600, color, letterSpacing: 1 }}>
          {assessment.toUpperCase()}
        </div>
      )}
    </div>
  );
}

function DimensionCard({ icon, label, score, summary, isPercent, onClick }) {
  const color = isPercent
    ? (parseFloat(score) >= 50 ? COLORS.green : parseFloat(score) >= 30 ? COLORS.amber : COLORS.red)
    : getScoreColor(score);
  const displayScore = isPercent ? score : typeof score === 'number' ? score.toFixed(1) : score;
  return (
    <div
      onClick={onClick}
      style={{
        background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8,
        padding: "14px 16px", flex: 1, minWidth: 120, textAlign: "center",
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{displayScore}</div>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4, lineHeight: 1.3 }}>{summary}</div>
    </div>
  );
}

function MitigationTable({ items }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${COLORS.cardBorder}` }}>
            {["#", "Risk", "Mitigation Action", "Owner", "Timing", "Success Indicator"].map(h => (
              <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: COLORS.textMuted, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${COLORS.cardBorder}`, borderLeft: `3px solid ${item.severity === "red" ? COLORS.red : COLORS.amber}` }}>
              <td style={{ padding: "10px", color: COLORS.textMuted, width: 30, verticalAlign: "top" }}>{i + 1}</td>
              <td style={{ padding: "10px", color: COLORS.text, fontWeight: 600, minWidth: 120, verticalAlign: "top" }}>{item.risk}</td>
              <td style={{ padding: "10px", color: COLORS.text, lineHeight: 1.5, minWidth: 200, verticalAlign: "top" }}>{item.action}</td>
              <td style={{ padding: "10px", color: COLORS.textMuted, minWidth: 100, verticalAlign: "top", whiteSpace: "nowrap" }}>{item.owner}</td>
              <td style={{ padding: "10px", color: COLORS.textMuted, minWidth: 90, verticalAlign: "top", whiteSpace: "nowrap" }}>{item.timing}</td>
              <td style={{ padding: "10px", color: COLORS.textMuted, lineHeight: 1.4, minWidth: 140, verticalAlign: "top" }}>{item.success}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ExecutiveSummary({ data, onEmployeeClick, onTabClick }) {
  const d = data;
  const [expandedMitigation, setExpandedMitigation] = useState(true);
  if (!d) return null;

  return (
    <div style={{ color: COLORS.text }}>

      {/* Hero Score */}
      <div style={{ textAlign: "center", padding: "16px 0 24px" }}>
        <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 2, marginBottom: 16 }}>Overall Team Score</div>
        <ScoreGauge score={d.scores.overall} />
        {d.opportunity?.name && (
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 8 }}>
            {d.opportunity.name} — {d.opportunity.client}
          </div>
        )}
      </div>

      {/* Team Strip */}
      {d.team?.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap", justifyContent: "center" }}>
          {d.team.map((m, i) => (
            <div key={i} style={{
              background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8,
              padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, minWidth: 180,
            }}>
              <MemberAvatar name={m.name} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  <ClickableName name={m.name} onClick={onEmployeeClick} />
                </div>
                <div style={{ fontSize: 10, color: COLORS.textMuted }}>{m.role}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dimension Scorecard */}
      <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
        <DimensionCard icon="⚙" label="Technical"    score={d.scores.technical.score}    summary={d.scores.technical.summary}  onClick={onTabClick ? () => onTabClick(1) : undefined} />
        <DimensionCard icon="🔬" label="Chemistry"   score={d.scores.chemistry.score}    summary={d.scores.chemistry.summary}  onClick={onTabClick ? () => onTabClick(2) : undefined} />
        <DimensionCard icon="💰" label="Financial"   score={d.scores.financial.pct || d.scores.financial.score?.toFixed(1)} summary={d.scores.financial.summary} isPercent={!!d.scores.financial.pct} onClick={onTabClick ? () => onTabClick(3) : undefined} />
        <DimensionCard icon="🕐" label="Availability" score={d.scores.availability.score} summary={d.scores.availability.summary} onClick={onTabClick ? () => onTabClick(4) : undefined} />
        <DimensionCard icon="⭐" label="Experience"  score={d.scores.experience.score}   summary={d.scores.experience.summary}  onClick={onTabClick ? () => onTabClick(5) : undefined} />
      </div>

      {/* Strengths & Tradeoffs */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: COLORS.green, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, marginTop: 0 }}>Top Strengths</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(d.strengths || []).map((s, i) => (
              <div key={i} style={{ borderLeft: `4px solid ${COLORS.green}`, background: `${COLORS.green}08`, padding: "8px 12px", borderRadius: "0 6px 6px 0", fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>
                <span style={{ color: COLORS.green, marginRight: 6 }}>✓</span>{s}
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: COLORS.amber, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, marginTop: 0 }}>Key Tradeoffs</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(d.tradeoffs || []).map((t, i) => (
              <div key={i} style={{ borderLeft: `4px solid ${COLORS.amber}`, background: `${COLORS.amber}08`, padding: "8px 12px", borderRadius: "0 6px 6px 0", fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>
                <span style={{ color: COLORS.amber, marginRight: 6 }}>⚠</span>
                {t.concern || t.tradeoff}
                {t.severity && (
                  <span style={{ marginLeft: 8, display: "inline-block", padding: "1px 8px", borderRadius: 10, background: `${COLORS.amber}20`, color: COLORS.amber, fontWeight: 600, fontSize: 10 }}>{t.severity}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mitigation Action Plan */}
      {(d.mitigationPlan?.length > 0) && (
        <div style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, marginBottom: 20, overflow: "hidden" }}>
          <div
            onClick={() => setExpandedMitigation(!expandedMitigation)}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer", borderBottom: expandedMitigation ? `1px solid ${COLORS.cardBorder}` : "none" }}
          >
            <h3 style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, textTransform: "uppercase", letterSpacing: 1, margin: 0 }}>
              🎯 Mitigation Action Plan
              <span style={{ marginLeft: 8, fontSize: 11, color: COLORS.amber, fontWeight: 400 }}>{d.mitigationPlan.length} action items</span>
            </h3>
            <span style={{ color: COLORS.textMuted, fontSize: 14, transform: expandedMitigation ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▼</span>
          </div>
          {expandedMitigation && (
            <div style={{ padding: "4px 12px 12px" }}>
              <MitigationTable items={d.mitigationPlan} />
            </div>
          )}
        </div>
      )}

      {/* Recommendation */}
      {d.recommendation && (
        <div style={{ background: `${COLORS.purple}10`, border: `1px solid ${COLORS.purple}30`, borderRadius: 8, padding: "14px 18px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.purple, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>Recommendation</div>
          <p style={{ fontSize: 13, lineHeight: 1.7, color: COLORS.text, margin: 0 }}>{d.recommendation}</p>
        </div>
      )}
    </div>
  );
}
